/* Hero slider: moves the slide track with the arrows and on a timer, keeps the pause button, the ring and the
   slide videos in sync, and follows swipes. The timer is a CSS animation (.hs__timer), so it pauses with the
   ring when the slider is off screen or the tab is hidden. */
if (!customElements.get('hero-slider')) {
  customElements.define(
    'hero-slider',
    class HeroSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.hs__track');
        this.slides = Array.from(this.querySelectorAll('.hs__slide'));
        this.timer = this.querySelector('.hs__timer');
        this.playButton = this.querySelector('.hs__play');
        this.index = 0;

        this.setOffset = this.setOffset.bind(this);
        this.setOffset();
        window.addEventListener('resize', this.setOffset);
        window.addEventListener('load', this.setOffset, { once: true });

        if (this.slides.length < 2) {
          this.playVideo();
          return;
        }

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.playing = this.dataset.autoplay === 'true' && !this.reducedMotion;
        this.classList.toggle('is-stopped', !this.playing);

        this.querySelectorAll('.hs__arrow').forEach((button) =>
          button.addEventListener('click', () => this.go(this.index + Number(button.dataset.dir), true))
        );
        if (this.playButton) this.playButton.addEventListener('click', () => this.toggle());
        if (this.timer) this.timer.addEventListener('animationend', () => this.go(this.index + 1));

        this.onScroll = this.onScroll.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        /* A swipe counts as the shopper taking over: restart the timer from the slide they land on */
        this.track.addEventListener('touchstart', () => this.classList.add('is-paused'), { passive: true });
        this.track.addEventListener('touchend', () => this.classList.remove('is-paused'), { passive: true });

        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            this.visible = entries[0].isIntersecting;
            this.classList.toggle('is-paused', !this.visible);
            this.syncVideos();
          });
          this.observer.observe(this);
        }

        this.update();
        this.restartTimer();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.setOffset);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        if (this.observer) this.observer.disconnect();
      }

      /* "Fill the screen": the height is the screen minus everything above the page content (announcement bar + header) */
      setOffset() {
        const main = document.getElementById('MainContent');
        const top = main ? main.getBoundingClientRect().top + window.scrollY : 0;
        this.style.setProperty('--hs-offset', `${Math.max(0, Math.min(top, 240))}px`);
      }

      go(target, fromUser = false) {
        const count = this.slides.length;
        const next = ((target % count) + count) % count;
        this.index = next;
        this.track.scrollTo({ left: next * this.track.clientWidth, behavior: this.reducedMotion ? 'auto' : 'smooth' });
        this.update();
        this.restartTimer();
        if (fromUser && this.playButton) this.playButton.blur();
      }

      /* Wait until the track has stopped moving (swipe or smooth scroll) before reading which slide it landed on */
      onScroll() {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          const width = this.track.clientWidth || 1;
          const landed = Math.round(this.track.scrollLeft / width);
          if (landed !== this.index) {
            this.index = landed;
            this.update();
            this.restartTimer();
          }
        }, 140);
      }

      update() {
        this.slides.forEach((slide, i) => {
          const on = i === this.index;
          slide.classList.toggle('is-active', on);
          slide.setAttribute('aria-hidden', on ? 'false' : 'true');
          slide.querySelectorAll('a, button').forEach((el) => {
            if (on) el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', '-1');
          });
        });
        this.syncVideos();
      }

      restartTimer() {
        this.classList.remove('is-timing');
        if (!this.playing) return;
        void this.offsetWidth; /* restart the CSS animations */
        this.classList.add('is-timing');
      }

      toggle() {
        this.playing = !this.playing;
        this.classList.toggle('is-stopped', !this.playing);
        if (this.playButton) {
          this.playButton.setAttribute(
            'aria-label',
            this.playing ? this.playButton.dataset.labelPause : this.playButton.dataset.labelPlay
          );
        }
        this.restartTimer();
        this.syncVideos();
      }

      /* Only the visible slide's video plays; the pause button also pauses it */
      syncVideos() {
        this.slides.forEach((slide, i) => {
          const video = slide.querySelector('video');
          if (!video) return;
          const shouldPlay = i === this.index && this.visible !== false && (this.playing || this.slides.length < 2);
          if (shouldPlay) {
            video.preload = 'auto';
            const attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      playVideo() {
        const video = this.querySelector('video');
        if (!video) return;
        const attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(() => {});
      }

      onBlockSelect(event) {
        const i = this.slides.indexOf(event.target);
        if (i < 0) return;
        this.editorHeld = this.playing;
        this.playing = false;
        this.classList.add('is-stopped');
        this.go(i);
      }

      onBlockDeselect(event) {
        if (this.slides.indexOf(event.target) < 0 || !this.editorHeld) return;
        this.editorHeld = false;
        this.toggle();
      }
    }
  );
}
